import { Anchor, Plane, Truck, FileCheck, Package, HardHat } from "lucide-react";
import { TID } from "@/lib/testIds";

const cards = [
  {
    key: "ocean",
    Icon: Anchor,
    title: "International Ocean Freight",
    blurb: "Reliable FCL and LCL shipping solutions connecting major ports worldwide.",
    items: ["Full Container Load (FCL)", "Less than Container Load (LCL)", "Import & Export", "Breakbulk Cargo", "Buyer & Seller Consolidation", "DDP · DDU · DAP Deliveries"],
    num: "01",
  },
  {
    key: "air",
    Icon: Plane,
    title: "International Air Freight",
    blurb: "Fast, secure, time-critical air cargo through our global airline network.",
    items: ["Airport to Airport", "Door to Door", "Express Air Freight", "Air Charter Services", "Dangerous Goods Handling", "Priority Cargo"],
    num: "02",
  },
  {
    key: "road",
    Icon: Truck,
    title: "Road Transportation",
    blurb: "Efficient domestic and cross-border transportation across the GCC and beyond.",
    items: ["Full Truck Load (FTL)", "Less than Truck Load (LTL)", "Cross Border Trucking", "Last Mile Delivery", "Dedicated Vehicles"],
    num: "03",
  },
  {
    key: "customs",
    Icon: FileCheck,
    title: "Customs Brokerage",
    blurb: "Smooth customs clearance with complete trade compliance.",
    items: ["Import Clearance", "Export Clearance", "Transit Documentation", "Customs Consultancy", "Trade Compliance Support"],
    num: "04",
  },
  {
    key: "warehousing",
    Icon: Package,
    title: "Warehousing & Distribution",
    blurb: "Flexible storage and fulfillment solutions designed for modern supply chains.",
    items: ["Secure Warehousing", "Inventory Management", "Order Fulfillment", "Distribution Services", "Pick & Pack"],
    num: "05",
  },
  {
    key: "project",
    Icon: HardHat,
    title: "Project Logistics",
    blurb: "Specialized transportation for oversized and complex cargo.",
    items: ["Heavy Lift Cargo", "Project Cargo", "Industrial Equipment", "Breakbulk Operations", "End-to-End Project Management"],
    num: "06",
  },
];

export default function Why() {
  return (
    <section
      id="why"
      data-testid={TID.why.section}
      className="bg-white border-t border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-0">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-20">
          <div className="md:col-span-6 reveal">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-600 mb-4">
              Comprehensive Logistics Solutions
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-900 leading-[1.02]">
              Six service lines.<br />
              <span className="text-slate-400">One trusted partner.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 reveal">
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              From urgent air freight to complex multimodal shipments, our logistics professionals build tailored supply chain solutions designed to keep your business moving — across every mode and every mile.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((c, i) => (
              <article
                key={c.key}
                data-testid={TID.why.card(c.key)}
                className={`group relative p-8 md:p-10 bg-white hover:bg-sky-50/50 transition-colors reveal
                  ${i % 3 !== 2 ? "lg:border-r" : ""}
                  ${i % 2 !== 1 ? "sm:border-r lg:border-r" : ""}
                  ${i % 3 === 2 ? "sm:border-r lg:border-r-0" : ""}
                  ${i < cards.length - 1 ? "border-b" : ""}
                  ${i === cards.length - 2 ? "lg:border-b-0" : ""}
                  ${i === cards.length - 3 ? "lg:border-b-0" : ""}
                  border-slate-200`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 text-white group-hover:bg-sky-400 group-hover:text-blue-900 transition-colors">
                    <c.Icon className="w-6 h-6" strokeWidth={1.5} />
                  </span>
                  <span className="font-mono text-xs text-slate-400 tracking-wider">{c.num}</span>
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-slate-900 mb-3 leading-tight">{c.title}</h3>
                <p className="text-base text-slate-600 mb-5 leading-relaxed">{c.blurb}</p>
                <ul className="space-y-2.5">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-slate-700 text-base">
                      <span className="w-1.5 h-1.5 mt-2 bg-sky-500 shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
                <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-sky-400 group-hover:w-full transition-all duration-500" />
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
